import { ColorPalette, Button, Popover } from "@wordpress/components";
import { useSetting } from "@wordpress/block-editor";
import { useState, useEffect, useMemo, useRef } from "@wordpress/element";
import { __ } from "@wordpress/i18n";

import {
  COLOR_PRESET_PREFIX,
  fromColorPreset,
  toColorPreset,
} from "../../utils/helpers";

import "./index.scss";

/**
 * Store a palette choice as the preset it came from, not the colour it happens
 * to resolve to today.
 *
 * WordPress's ColorPalette reports the resolved value — pick "Primary" and the
 * callback receives `#2F70B3`, with nothing left to say which swatch produced
 * it. Saved that way, the block is pinned to a hex: retheming the site, or
 * switching a style variation, moves every preset except the ones already baked
 * into content. Matching the value back to its swatch here is the last point
 * where the slug is still recoverable.
 *
 * `var:preset|color|<slug>` is the same serialisation core uses inside block
 * `style` attributes, and what utils/helpers.js `resolvePresetValue` expects, so
 * consumers turn it into CSS with the helper they already use for spacing.
 * Custom colours have no slug and pass through as the raw value.
 */
export default function ColorPicker({ color, onChange }) {
  const themeColors = useSetting("color.palette.theme");
  const defaultColors = useSetting("color.palette.default");
  const [open, setOpen] = useState(false);
  const toggleVisible = (e) => {
    if (!open) {
      setOpen(true);
    } else {
      if (
        buttonRef.current?.contains(e.target) &&
        !popoverRef.current?.parentNode?.contains(e.target)
      ) {
        setOpen(false);
      }
    }
  };
  const colors = [
    {
      name: __("Theme", "brandy"),
      colors: themeColors,
    },
    {
      name: __("Default", "brandy"),
      colors: defaultColors,
    },
  ];

  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  const paletteColors = useMemo(
    () => [...(themeColors || []), ...(defaultColors || [])],
    [themeColors, defaultColors]
  );

  // The palette needs a concrete colour to mark a swatch selected and the
  // indicator needs one to paint; neither understands the stored reference.
  const resolved = useMemo(
    () => fromColorPreset(color, paletteColors),
    [color, paletteColors]
  );

  const handleChange = (value) => onChange(toColorPreset(value, paletteColors));

  useEffect(() => {
    function clickOutside(e) {
      // ColorPalette's "Custom color" picker — the only way to type a hex —
      // opens in its own popover portaled to the document body, so it is NOT
      // inside popoverRef's subtree. Without this guard, the first click into
      // the hex field counted as an outside click and closed this popover,
      // unmounting the picker mid-edit: palette swatches worked, custom hex was
      // impossible. Any click landing in a WP popover (ours or a nested one)
      // is therefore treated as inside.
      if (e.target?.closest?.(".components-popover")) {
        return;
      }

      if (
        !buttonRef.current?.contains(e.target) &&
        !popoverRef.current?.parentNode?.contains(e.target)
      ) {
        setOpen(false);
      }
    }

    window.addEventListener("click", clickOutside);
    return () => {
      window.removeEventListener("click", clickOutside);
    };
  }, [buttonRef.current, popoverRef.current, setOpen]);

  return (
    <div className="color-picker">
      <div className="color-picker__head">
        <div
          className="color-indicator"
          style={{ background: resolved }}
          onClick={toggleVisible}
          ref={buttonRef}
        >
          {open && (
            <Popover className="brandy-color-popover" ref={popoverRef}>
              <div className="brandy-color-modal">
                <ColorPalette
                  colors={colors}
                  value={resolved}
                  onChange={handleChange}
                />
              </div>
            </Popover>
          )}
        </div>
      </div>
      {/* A preset reads as its slug — the point of choosing one is that the
          colour is not fixed, so showing today's hex would misreport it. */}
      <span className="color-picker__tail">
        {color?.startsWith(COLOR_PRESET_PREFIX)
          ? color.slice(COLOR_PRESET_PREFIX.length)
          : color?.replace("#", "").replace("var(--", "").replace(")", "") ??
            "Initial"}
      </span>
    </div>
  );
}
